import { useState } from 'react';
import { useContactEmails, useIntegrations, useCreateActivity } from '../hooks';
import { useMailFolders, useMailsInFolder } from '../../o365/hooks';
import type { M365MailFolder } from '../../o365/api';
import { integrationsApi } from '../api';
import type { M365Email, M365EmailAddress } from '../types';
import styles from './EmailsTab.module.css';

// Ordner-Sortierung analog zum Dashboard
const FOLDER_PRIORITY: Record<string, number> = {
  'Posteingang': 0, 'Inbox': 0,
  'Gesendete Elemente': 1, 'Sent Items': 1, 'Gesendet': 1,
  'Drafts': 2,
  'Archiv': 3, 'Archive': 3,
  'Junk-E-Mail': 4, 'Junk Email': 4,
  'Deleted Items': 5,
  'Ausgang': 6, 'Outbox': 6,
};
// Add German umlauts separately to avoid duplicate key errors
FOLDER_PRIORITY['Entw\u00fcrfe'] = 2;
FOLDER_PRIORITY['Gel\u00f6schte Elemente'] = 5;

function sortFolders(folders: M365MailFolder[]): M365MailFolder[] {
  return [...folders].sort((a, b) => {
    const pa = FOLDER_PRIORITY[a.displayName] ?? 99;
    const pb = FOLDER_PRIORITY[b.displayName] ?? 99;
    return pa - pb || a.displayName.localeCompare(b.displayName);
  });
}

interface Props {
  contactId: string;
  contactEmail?: string;
}

function formatEmailDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatEmailTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function recipientsText(
  recipients: Array<{ emailAddress: M365EmailAddress }>,
): string {
  return recipients
    .map((r) => r.emailAddress.name ?? r.emailAddress.address ?? '—')
    .join(', ');
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/* ── Detail-Popup (Outlook-ähnlich) ───────────────────────────────────────── */

function EmailDetailModal({
  email,
  contactId,
  onClose,
}: {
  email: M365Email;
  contactId: string;
  onClose: () => void;
}) {
  const createActivity = useCreateActivity();
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [comment, setComment] = useState('');

  const fromName =
    email.from?.emailAddress?.name ?? email.from?.emailAddress?.address ?? '—';
  const fromAddr = email.from?.emailAddress?.address ?? '';
  const toText = email.toRecipients?.length
    ? recipientsText(email.toRecipients)
    : '—';
  const ccText = (email as any).ccRecipients?.length
    ? recipientsText((email as any).ccRecipients)
    : '';

  // HTML-Tags entfernen fuer Plain-Text Darstellung
  function stripHtml(html: string): string {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  async function handleSave() {
    if (saveState === 'saved') return;
    setSaveState('saving');
    try {
      // Vollstaendiges Abbild der Email
      const lines: string[] = [];
      lines.push(`Von: ${fromName}${fromAddr ? ` <${fromAddr}>` : ''}`);
      lines.push(`An: ${toText}`);
      if (ccText) lines.push(`CC: ${ccText}`);
      lines.push(`Datum: ${formatEmailDate(email.receivedDateTime)}`);
      if (email.hasAttachments) lines.push('Anhaenge: Ja');
      lines.push('');
      lines.push('--- Inhalt ---');
      // Vollstaendigen Body verwenden wenn verfuegbar, sonst bodyPreview
      if (email.body?.content) {
        const bodyText = email.body.contentType === 'html'
          ? stripHtml(email.body.content)
          : email.body.content;
        lines.push(bodyText);
      } else if (email.bodyPreview) {
        lines.push(email.bodyPreview);
      }
      if (comment.trim()) {
        lines.push('');
        lines.push('--- Kommentar ---');
        lines.push(comment.trim());
      }

      await createActivity.mutateAsync({
        contactId,
        type: 'EMAIL',
        subject: email.subject ?? '(kein Betreff)',
        description: lines.join('\n'),
        scheduledAt: email.receivedDateTime,
        completedAt: email.receivedDateTime,
      });
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header with actions */}
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h2 className={styles.modalSubject}>{email.subject ?? '(kein Betreff)'}</h2>
            {!email.isRead && (
              <span className={styles.modalUnreadBadge}>Ungelesen</span>
            )}
          </div>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Schließen"
          >
            ×
          </button>
        </div>

        {/* Action buttons */}
        <div style={{
          display: 'flex', gap: '0.5rem', padding: '0 1.25rem',
          marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center',
        }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveState === 'saving' || saveState === 'saved'}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: 600,
              background: saveState === 'saved' ? '#16a34a' : 'var(--color-primary)',
              color: 'white', border: 'none', borderRadius: 'var(--radius-sm)',
              cursor: saveState === 'saving' || saveState === 'saved' ? 'default' : 'pointer',
              opacity: saveState === 'saving' ? 0.6 : 1,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 1v12M1 7h12" />
            </svg>
            {saveState === 'idle' && 'In Kontakt speichern'}
            {saveState === 'saving' && 'Speichern...'}
            {saveState === 'saved' && 'Gespeichert'}
            {saveState === 'error' && 'Fehler - erneut'}
          </button>
          <a
            href={email.webLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: 600,
              background: '#0078d4', color: 'white', border: 'none',
              borderRadius: 'var(--radius-sm)', textDecoration: 'none',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2H2a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1V8" />
              <path d="M10 1h5v5" />
              <path d="M6 10L15 1" />
            </svg>
            In Outlook öffnen
          </a>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Meta: Von / An / Datum / Anhang */}
          <div className={styles.metaTable}>
            <span className={styles.metaLabel}>Von</span>
            <span className={styles.metaValue}>{fromName}</span>

            <span className={styles.metaLabel}>An</span>
            <span className={styles.metaValue}>{toText}</span>

            <span className={styles.metaLabel}>Datum</span>
            <span className={styles.metaValue}>{formatEmailDate(email.receivedDateTime)}</span>

            {email.hasAttachments && (
              <>
                <span className={styles.metaLabel}>Anhang</span>
                <span className={styles.metaValue}>Anhaenge vorhanden</span>
              </>
            )}
          </div>

          {/* E-Mail-Inhalt */}
          {email.body?.content ? (
            email.body.contentType === 'html' ? (
              <div
                className={styles.bodyPreview}
                dangerouslySetInnerHTML={{ __html: email.body.content }}
                style={{ maxHeight: 400, overflow: 'auto' }}
              />
            ) : (
              <div className={styles.bodyPreview} style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>
                {email.body.content}
              </div>
            )
          ) : email.bodyPreview ? (
            <>
              <div className={styles.bodyPreview}>{email.bodyPreview}</div>
              <p className={styles.bodyHint}>
                Vorschau - vollständige E-Mail in Outlook öffnen
              </p>
            </>
          ) : (
            <p className={styles.bodyHint}>Kein Vorschautext verfuegbar</p>
          )}

          {/* Kommentar */}
          {saveState !== 'saved' && (
            <div style={{ marginTop: '0.75rem' }}>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optionaler Kommentar beim Speichern..."
                style={{
                  width: '100%', padding: '0.5rem 0.75rem',
                  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                  fontSize: '0.8125rem', resize: 'vertical', minHeight: 48,
                  background: 'var(--color-bg-card)', color: 'var(--color-text)',
                  boxSizing: 'border-box', outline: 'none',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Hauptkomponente ──────────────────────────────────────────────────────── */

export function EmailsTab({ contactId, contactEmail }: Props) {
  const { data: integrationsData } = useIntegrations();
  const isConnected =
    (integrationsData?.data?.some(
      (i) => i.provider === 'MICROSOFT_365' && i.connected,
    ) ?? false);

  // Ordner laden
  const { data: foldersData } = useMailFolders();
  const allFolders = sortFolders(foldersData?.data ?? []);

  // Alle = Suche ueber alle Ordner (bisheriges Verhalten), sonst Ordner-spezifisch
  const [activeFolderId, setActiveFolderId] = useState<string>('');
  const [selectedEmail, setSelectedEmail] = useState<M365Email | null>(null);

  // Kontakt-spezifische Suche (alle Ordner)
  const { data: searchData, isLoading: searchLoading, error: searchError } = useContactEmails(contactId);

  // Ordner-spezifische Emails
  const { data: folderData, isLoading: folderLoading } = useMailsInFolder(activeFolderId, 0);

  // Emails je nach Modus
  const emailLower = (contactEmail ?? '').toLowerCase();
  let emails: M365Email[];
  let isLoading: boolean;
  let error: unknown;

  if (!activeFolderId) {
    // "Alle" — kontakt-spezifische Suche
    emails = searchData?.data ?? [];
    isLoading = searchLoading;
    error = searchError;
  } else {
    // Ordner-spezifisch — client-seitig nach Kontakt filtern
    const raw = (folderData?.data ?? []) as unknown as M365Email[];
    emails = emailLower
      ? raw.filter((e: any) => {
          const from = e.from?.emailAddress?.address?.toLowerCase() ?? '';
          if (from === emailLower) return true;
          const to = ((e as any).toRecipients ?? []) as Array<{ emailAddress: { address: string } }>;
          if (to.some((r) => r.emailAddress?.address?.toLowerCase() === emailLower)) return true;
          const cc = ((e as any).ccRecipients ?? []) as Array<{ emailAddress: { address: string } }>;
          if (cc.some((r) => r.emailAddress?.address?.toLowerCase() === emailLower)) return true;
          return false;
        })
      : raw;
    isLoading = folderLoading;
    error = null;
  }

  if (!isConnected) {
    return (
      <div style={{ padding: '1.5rem 0', textAlign: 'center' }}>
        <p
          style={{
            color: 'var(--color-text-muted)',
            fontSize: '0.9375rem',
            marginBottom: '1rem',
          }}
        >
          Verbinden Sie Microsoft 365, um E-Mails zu diesem Kontakt zu sehen.
        </p>
        <button
          type="button"
          onClick={() => integrationsApi.connectM365()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.4375rem 1rem',
            background: 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Microsoft 365 verbinden
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <p
        style={{
          color: 'var(--color-text-muted)',
          fontSize: '0.875rem',
          padding: '1rem 0',
        }}
      >
        Laden…
      </p>
    );
  }

  if (error) {
    return (
      <p
        style={{
          color: 'var(--color-error)',
          fontSize: '0.875rem',
          padding: '1rem 0',
        }}
      >
        E-Mails konnten nicht geladen werden.
      </p>
    );
  }

  // Folder icon mapping — clean SVG icons
  const folderIcon = (name: string): React.ReactNode => {
    const s = { width: 14, height: 14, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.4, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
    const n = name.toLowerCase();
    if (n.includes('inbox') || n.includes('posteingang')) return <svg {...s}><rect x="2" y="2" width="12" height="12" rx="2"/><path d="M2 10h4l1 2h2l1-2h4"/></svg>;
    if (n.includes('sent') || n.includes('gesendet')) return <svg {...s}><path d="M14 2L7 9M14 2l-5 12-2-5-5-2 12-5z"/></svg>;
    if (n.includes('draft') || n.includes('entwurf') || n.includes('entw')) return <svg {...s}><path d="M11 1l4 4-9 9H2v-4z"/><path d="M9 3l4 4"/></svg>;
    if (n.includes('archiv') || n.includes('archive')) return <svg {...s}><rect x="1" y="2" width="14" height="3" rx="1"/><path d="M2 5v8a1 1 0 001 1h10a1 1 0 001-1V5M6 8h4"/></svg>;
    if (n.includes('junk') || n.includes('spam')) return <svg {...s} stroke="#d97706"><path d="M8 1l7 13H1L8 1z"/><path d="M8 6v3M8 11.5v.5"/></svg>;
    if (n.includes('deleted') || n.includes('gelöscht') || n.includes('gel')) return <svg {...s}><path d="M2 4h12M5.3 4V2.7A1.3 1.3 0 016.7 1.3h2.6a1.3 1.3 0 011.4 1.4V4m2 0v9.3a1.3 1.3 0 01-1.3 1.4H4.7a1.3 1.3 0 01-1.4-1.4V4h9.4z"/></svg>;
    if (n.includes('outbox') || n.includes('ausgang')) return <svg {...s}><rect x="2" y="2" width="12" height="12" rx="2"/><path d="M8 10V5M5.5 7.5L8 5l2.5 2.5"/></svg>;
    return <svg {...s}><path d="M2 4a1 1 0 011-1h3l2 2h5a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1V4z"/></svg>;
  };

  const folderTreeStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.375rem 0.625rem',
    fontSize: '0.8125rem', fontWeight: active ? 600 : 400,
    background: active ? 'var(--color-primary-light, #eff6ff)' : 'transparent',
    color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
    border: 'none', borderRadius: 'var(--radius-sm)',
    cursor: 'pointer', width: '100%', textAlign: 'left',
    borderLeft: active ? '3px solid var(--color-primary)' : '3px solid transparent',
  });

  return (
    <>
      <div style={{ display: 'flex', gap: '1rem' }}>
        {/* Ordner-Baum links */}
        {allFolders.length > 0 && (
          <div style={{
            minWidth: 180, maxWidth: 200, flexShrink: 0,
            borderRight: '1px solid var(--color-border)',
            paddingRight: '0.75rem',
          }}>
            <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.5rem', letterSpacing: '0.05em' }}>
              Ordner
            </div>
            <button
              onClick={() => setActiveFolderId('')}
              style={folderTreeStyle(!activeFolderId)}
            >
              <span style={{ fontSize: '0.875rem' }}>📋</span>
              <span style={{ flex: 1 }}>Alle</span>
            </button>
            {allFolders.map((f) => (
              <button
                key={f.id}
                onClick={() => setActiveFolderId(activeFolderId === f.id ? '' : f.id)}
                style={folderTreeStyle(activeFolderId === f.id)}
              >
                <span style={{ fontSize: '0.875rem' }}>{folderIcon(f.displayName)}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.displayName}</span>
                {f.unreadItemCount > 0 && (
                  <span style={{
                    fontSize: '0.625rem', fontWeight: 700,
                    background: 'var(--color-primary)', color: 'white',
                    borderRadius: '999px', padding: '0 0.3rem', minWidth: 16, textAlign: 'center',
                    flexShrink: 0,
                  }}>
                    {f.unreadItemCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* E-Mails rechts */}
        <div style={{ flex: 1, minWidth: 0 }}>
      {emails.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', padding: '1rem 0' }}>
          Keine E-Mails gefunden.
        </p>
      ) : (
      <div className={styles.emailList}>
        {emails.map((email) => (
          <button
            key={email.id}
            type="button"
            className={`${styles.emailItem}${!email.isRead ? ` ${styles.emailItemUnread}` : ''}`}
            onClick={() => setSelectedEmail(email)}
          >
            {!email.isRead ? (
              <span className={styles.unreadDot} aria-hidden="true" />
            ) : (
              <span className={styles.readSpacer} />
            )}
            <div className={styles.emailMeta}>
              <div className={styles.emailRow1}>
                <span
                  className={`${styles.emailSubject}${!email.isRead ? ` ${styles.emailSubjectUnread}` : ''}`}
                >
                  {email.subject ?? '(kein Betreff)'}
                </span>
                <span className={styles.emailTime}>
                  {formatEmailTime(email.receivedDateTime)}
                </span>
              </div>
              <div className={styles.emailFrom}>
                Von:{' '}
                {email.from?.emailAddress?.name ??
                  email.from?.emailAddress?.address ??
                  '—'}
              </div>
              {email.bodyPreview && (
                <div className={styles.emailPreview}>{email.bodyPreview}</div>
              )}
            </div>
            {email.hasAttachments && (
              <span className={styles.attachBadge} title="Anhänge vorhanden">
                📎
              </span>
            )}
          </button>
        ))}
      </div>
      )}
        </div>
      </div>

      {selectedEmail !== null && (
        <EmailDetailModal
          email={selectedEmail}
          contactId={contactId}
          onClose={() => setSelectedEmail(null)}
        />
      )}
    </>
  );
}
